import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface Tab {
  value: string;
  label: string;
  image?: string;
  /**
   * Optional ReactNode rendered in the 27px circular slot. Used for inline
   * SVGs (MRI person icon) or composed icons (membership pill).
   * Takes precedence over `image`.
   */
  icon?: React.ReactNode;
}

export interface TabSwitcherProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (value: string) => void;
  className?: string;
  /**
   * "lg": a size up, for a switch that opens the page rather than sits in
   * it (membership-first's tests). The icon slot takes whatever the icon's
   * size is.
   */
  size?: "default" | "lg";
}

export function TabSwitcher({
  tabs,
  activeTab,
  onChange,
  className,
  size = "default",
}: TabSwitcherProps) {
  const lg = size === "lg";
  const hasVisuals = tabs.some((tab) => tab.image || tab.icon);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [pill, setPill] = useState<{ left: number; width: number } | null>(
    null,
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    const active = buttonRefs.current[activeTab];
    if (!container || !active) return;

    const measureRect = () => {
      const cRect = container.getBoundingClientRect();
      const bRect = active.getBoundingClientRect();
      return { left: bRect.left - cRect.left, width: bRect.width };
    };

    setPill(measureRect());

    // ResizeObserver keeps the pill pinned to the active button as the
    // container or button width changes (font load, label collapse on
    // mobile breakpoint, etc.).
    const ro = new ResizeObserver(() => setPill(measureRect()));
    ro.observe(container);
    ro.observe(active);

    return () => ro.disconnect();
  }, [activeTab, tabs.length]);

  return (
    <div
      ref={containerRef}
      className={cn(
        // overflow-hidden clips the pill within the rounded track so it
        // can never bleed outside the gray surround, even mid-measurement.
        "relative inline-flex max-w-full gap-1 overflow-hidden rounded-full p-1",
        hasVisuals
          ? "bg-[rgba(106,133,161,0.13)] backdrop-blur-[30px]"
          : "bg-neutral-100",
        className,
      )}
      role="tablist"
      aria-label="Tab switcher"
    >
      {pill && (
        <span
          aria-hidden="true"
          /* Short, smooth left/width slide. The container is overflow-hidden
             so the pill can never bleed past the rounded track, even mid-
             transition. */
          className="absolute top-1 bottom-1 rounded-full bg-white transition-[left,width] duration-[200ms] ease-[cubic-bezier(0.33,1,0.68,1)]"
          style={{ left: pill.left, width: pill.width }}
        />
      )}
      {tabs.map((tab) => {
        const isActive = tab.value === activeTab;
        return (
          <button
            key={tab.value}
            ref={(el) => {
              buttonRefs.current[tab.value] = el;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.value)}
            className={cn(
              "relative min-w-0 cursor-pointer rounded-full normal-case tracking-normal",
              "transition-all duration-[240ms] ease-[cubic-bezier(0.33,1,0.68,1)]",
              hasVisuals
                ? lg
                  ? "flex h-10 items-center gap-1.5 py-1 pl-1 pr-2.5 md:h-11 md:gap-2 md:pr-4"
                  : "flex h-9 items-center gap-1.5 py-[3px] pl-1 pr-4"
                : "px-5 py-2",
              lg
                ? "text-sm font-medium md:text-[15px]"
                : "text-sm font-medium max-md:text-[13px]",
              isActive
                ? "text-neutral-900"
                : "text-neutral-500 hover:text-neutral-700",
            )}
          >
            {tab.icon && (
              <span className="relative z-10 shrink-0">{tab.icon}</span>
            )}
            {tab.image && !tab.icon && (
              <img
                src={tab.image}
                alt=""
                width={27}
                height={27}
                className="relative z-10 size-[27px] shrink-0 rounded-full object-cover"
              />
            )}
            <span className="relative z-10 truncate">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}
